import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { createPluginRegistration } from '@embedpdf/core/dist/index.cjs';
import { EmbedPDF } from '@embedpdf/core/dist/react/index.cjs';
import { usePdfiumEngine } from '@embedpdf/engines/dist/react/index.cjs';
import {
  DocumentContent,
  DocumentManagerPlugin,
  DocumentManagerPluginPackage,
} from '@embedpdf/plugin-document-manager/dist/react/index.cjs';
import {
  Scroller,
  ScrollPluginPackage,
  ScrollStrategy,
  useScrollCapability,
} from '@embedpdf/plugin-scroll/dist/react/index.cjs';
import { RenderLayer, RenderPluginPackage } from '@embedpdf/plugin-render/dist/react/index.cjs';
import { SpreadMode, SpreadPluginPackage } from '@embedpdf/plugin-spread/dist/react/index.cjs';
import { TilingLayer, TilingPluginPackage } from '@embedpdf/plugin-tiling/dist/react/index.cjs';
import {
  Viewport,
  ViewportPluginPackage,
} from '@embedpdf/plugin-viewport/dist/react/index.cjs';
import {
  useZoom,
  ZoomMode,
  ZoomPluginPackage,
} from '@embedpdf/plugin-zoom/dist/react/index.cjs';

const styles = require('../librarian_reader/librarian_reader.styl');
const cx = classNames.bind(styles);

const BASE_PATH = typeof LIBRARIAN_BASE_PATH !== 'undefined' ? LIBRARIAN_BASE_PATH : '/librarian';
const ZOOM_MIN_PERCENT = 100;
const ZOOM_MAX_PERCENT = 200;
const ZOOM_STEP_PERCENT = 10;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const buildPdfUrl = (assetId) => `${BASE_PATH}/pdf/${encodeURIComponent(assetId)}`;

const DocumentReadyObserver = ({ documentId, onDocumentReady }) => {
  const { provides: scrollCapability } = useScrollCapability();

  useEffect(() => {
    const scope = scrollCapability ? scrollCapability.forDocument(documentId) : null;
    if (!scope) {
      return undefined;
    }

    const checkReady = () => {
      if (scope.getTotalPages() > 0) {
        onDocumentReady();
      }
    };

    checkReady();
    const unsubscribePageChange = scope.onPageChange(checkReady);
    const unsubscribeLayoutChange = scope.onLayoutChange(checkReady);

    return () => {
      if (unsubscribePageChange) {
        unsubscribePageChange();
      }
      if (unsubscribeLayoutChange) {
        unsubscribeLayoutChange();
      }
    };
  }, [scrollCapability, documentId, onDocumentReady]);

  return null;
};

DocumentReadyObserver.propTypes = {
  documentId: PropTypes.string.isRequired,
  onDocumentReady: PropTypes.func,
};

DocumentReadyObserver.defaultProps = {
  onDocumentReady: () => {},
};

const ReaderControls = ({
  documentId,
  disabled,
  showUpdating,
}) => {
  const { provides: zoom, state: zoomState } = useZoom(documentId);
  const { provides: scrollCapability } = useScrollCapability();
  const scroll = scrollCapability ? scrollCapability.forDocument(documentId) : null;
  const [scrollState, setScrollState] = useState({
    currentPage: 1,
    totalPages: 0,
  });

  const currentZoomLevel = Number.isFinite(zoomState.currentZoomLevel)
    ? zoomState.currentZoomLevel
    : 1;
  const zoomPercent = clamp(
    Math.round(currentZoomLevel * 100),
    ZOOM_MIN_PERCENT,
    ZOOM_MAX_PERCENT,
  );

  const totalPages = Number.isFinite(scrollState.totalPages) ? scrollState.totalPages : 0;
  const currentPage = Number.isFinite(scrollState.currentPage) ? scrollState.currentPage : 0;

  useEffect(() => {
    const scope = scrollCapability ? scrollCapability.forDocument(documentId) : null;
    if (!scope) {
      setScrollState({
        currentPage: 1,
        totalPages: 0,
      });
      return undefined;
    }

    const updateScrollState = () => {
      const nextState = {
        currentPage: scope.getCurrentPage(),
        totalPages: scope.getTotalPages(),
      };
      setScrollState((previousState) => {
        if (
          previousState.currentPage === nextState.currentPage
          && previousState.totalPages === nextState.totalPages
        ) {
          return previousState;
        }
        return nextState;
      });
    };

    updateScrollState();
    const unsubscribePageChange = scope.onPageChange(updateScrollState);
    const unsubscribeLayoutChange = scope.onLayoutChange(updateScrollState);
    const unsubscribePageChangeState = scrollCapability.onPageChangeState
      ? scrollCapability.onPageChangeState((event) => {
        if (!event || event.documentId !== documentId || !event.state || !event.state.isChanging) {
          return;
        }
        const targetPage = Number(event.state.targetPage);
        if (!Number.isFinite(targetPage) || targetPage < 1) {
          return;
        }
        setScrollState((previousState) => ({
          currentPage: targetPage,
          totalPages: previousState.totalPages,
        }));
      })
      : null;

    return () => {
      if (unsubscribePageChange) {
        unsubscribePageChange();
      }
      if (unsubscribeLayoutChange) {
        unsubscribeLayoutChange();
      }
      if (unsubscribePageChangeState) {
        unsubscribePageChangeState();
      }
    };
  }, [scrollCapability, documentId]);

  const onZoomChange = (event) => {
    if (!zoom) {
      return;
    }
    const nextZoomPercent = Number(event.target.value);
    if (Number.isNaN(nextZoomPercent)) {
      return;
    }
    zoom.requestZoom(nextZoomPercent / 100);
  };

  const goPrevious = () => {
    if (!scroll) {
      return;
    }
    scroll.scrollToPreviousPage();
  };

  const goNext = () => {
    if (!scroll) {
      return;
    }
    scroll.scrollToNextPage();
  };

  return (
    <div className={cx('librarian-reader__controls')}>
      <div className={cx('librarian-reader__zoom-controls')}>
        <span className={cx('librarian-reader__zoom-label')}>Zoom</span>
        <input
          className={cx('librarian-reader__zoom-slider')}
          type="range"
          min={ZOOM_MIN_PERCENT}
          max={ZOOM_MAX_PERCENT}
          step={ZOOM_STEP_PERCENT}
          value={zoomPercent}
          onChange={onZoomChange}
          disabled={disabled || !zoom}
        />
        <span className={cx('librarian-reader__zoom-value')}>{zoomPercent}%</span>
      </div>
      <div className={cx('librarian-reader__pager-controls')}>
        <button
          type="button"
          onClick={goPrevious}
          disabled={disabled || !scroll || currentPage <= 1}
        >
          ‹
        </button>
        <div className={cx('librarian-reader__page-info')}>
          {totalPages ? `Page ${currentPage} of ${totalPages}` : 'Preparing pages…'}
        </div>
        <button
          type="button"
          onClick={goNext}
          disabled={disabled || !scroll || currentPage >= totalPages}
        >
          ›
        </button>
      </div>
      <div
        className={cx('librarian-reader__updating-hint', {
          'is-visible': showUpdating,
        })}
      >
        Updating…
      </div>
    </div>
  );
};

ReaderControls.propTypes = {
  documentId: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  showUpdating: PropTypes.bool,
};

ReaderControls.defaultProps = {
  disabled: false,
  showUpdating: false,
};

const LibrarianReaderEmbed = ({ assetId }) => {
  const pdfUrl = useMemo(() => (assetId ? buildPdfUrl(assetId) : null), [assetId]);
  const { engine, isLoading: isEngineLoading, error: engineError } = usePdfiumEngine({ worker: false });
  const openedUrlRef = useRef(null);
  const [initError, setInitError] = useState(null);
  const [isDocumentReady, setIsDocumentReady] = useState(false);

  useEffect(() => {
    openedUrlRef.current = null;
    setInitError(null);
    setIsDocumentReady(false);
  }, [pdfUrl]);

  const markDocumentReady = useCallback(() => {
    setIsDocumentReady(true);
  }, []);

  const plugins = useMemo(() => {
    return [
      createPluginRegistration(DocumentManagerPluginPackage),
      createPluginRegistration(ViewportPluginPackage, {
        viewportGap: 0,
      }),
      createPluginRegistration(ScrollPluginPackage, {
        defaultStrategy: ScrollStrategy.Vertical,
        defaultPageGap: 18,
      }),
      createPluginRegistration(RenderPluginPackage),
      createPluginRegistration(TilingPluginPackage, {
        tileSize: 768,
        overlapPx: 2.5,
        extraRings: 0,
      }),
      createPluginRegistration(SpreadPluginPackage, {
        defaultSpreadMode: SpreadMode.Odd,
      }),
      createPluginRegistration(ZoomPluginPackage, {
        defaultZoomLevel: ZoomMode.FitPage,
        minZoom: ZOOM_MIN_PERCENT / 100,
        maxZoom: ZOOM_MAX_PERCENT / 100,
      }),
    ];
  }, []);

  const handleInitialized = async (registry) => {
    if (!registry || !pdfUrl || openedUrlRef.current === pdfUrl) {
      return;
    }

    const documentManagerPlugin = registry.getPlugin(DocumentManagerPlugin.id);
    if (!documentManagerPlugin || !documentManagerPlugin.provides) {
      setInitError('Document manager plugin is unavailable.');
      return;
    }

    openedUrlRef.current = pdfUrl;
    setInitError(null);

    try {
      const openedDocument = await documentManagerPlugin
        .provides()
        .openDocumentUrl({
          url: pdfUrl,
          mode: 'full-fetch',
          requestOptions: {
            credentials: 'include',
          },
        })
        .toPromise();
      // Keep this console log while validating EmbedPDF integration.
      // eslint-disable-next-line no-console
      console.log('[LibrarianReaderEmbed] openDocumentUrl resolved', openedDocument);
    } catch (error) {
      openedUrlRef.current = null;
      const message = (error && error.message) ? error.message : 'Failed to open PDF document.';
      setInitError(message);
      // Keep this console log while validating EmbedPDF integration.
      // eslint-disable-next-line no-console
      console.error('[LibrarianReaderEmbed] openDocumentUrl failed', error);
    }
  };

  let statusMessage = null;
  if (!assetId) {
    statusMessage = 'No article selected.';
  } else if (engineError) {
    statusMessage = "We couldn't load this reader right now.";
  } else if (initError) {
    statusMessage = `Reader error: ${initError}`;
  } else if (isEngineLoading || !engine) {
    statusMessage = 'Loading...';
  }

  return (
    <div className={cx('librarian-reader')}>
      <div className={cx('librarian-reader__viewer', 'librarian-reader__viewer--embed')}>
        {statusMessage ? <div className={cx('librarian-reader__status')}>{statusMessage}</div> : null}
        {!statusMessage ? (
          <div className={cx('librarian-reader__embed-root')}>
            <EmbedPDF key={pdfUrl} engine={engine} plugins={plugins} onInitialized={handleInitialized}>
              {({ activeDocumentId }) => (
                activeDocumentId ? (
                  <DocumentContent documentId={activeDocumentId}>
                    {({ isLoading, isError, isLoaded }) => {
                      const loadingMessage = isError
                        ? "We couldn't load this article right now."
                        : 'Loading...';
                      const shouldShowLoading = !isLoaded || !isDocumentReady;

                      return (
                        <div className={cx('librarian-reader__embed-content')}>
                          {shouldShowLoading ? (
                            <div className={cx('librarian-reader__status')}>{loadingMessage}</div>
                          ) : null}
                          {isLoaded ? (
                            <DocumentReadyObserver
                              documentId={activeDocumentId}
                              onDocumentReady={markDocumentReady}
                            />
                          ) : null}
                          {isLoaded ? (
                            <Viewport
                              documentId={activeDocumentId}
                              className={cx('librarian-reader__embed-viewport')}
                              style={{
                                width: '100%',
                                height: '100%',
                                flexGrow: 1,
                                minHeight: 0,
                                overflow: 'auto',
                                backgroundColor: 'transparent',
                              }}
                            >
                              <Scroller
                                documentId={activeDocumentId}
                                renderPage={({ pageIndex }) => (
                                  <div
                                    className={cx('librarian-reader__embed-page')}
                                    style={{ width: '100%', height: '100%' }}
                                  >
                                    <RenderLayer
                                      documentId={activeDocumentId}
                                      pageIndex={pageIndex}
                                      scale={1}
                                    />
                                    <TilingLayer
                                      documentId={activeDocumentId}
                                      pageIndex={pageIndex}
                                    />
                                  </div>
                                )}
                              />
                            </Viewport>
                          ) : null}
                          {!shouldShowLoading ? (
                            <ReaderControls
                              documentId={activeDocumentId}
                              disabled={!isLoaded || !isDocumentReady}
                              showUpdating={!isError && isLoading}
                            />
                          ) : null}
                        </div>
                      );
                    }}
                  </DocumentContent>
                ) : (
                  <div className={cx('librarian-reader__status')}>Loading...</div>
                )
              )}
            </EmbedPDF>
          </div>
        ) : null}
      </div>
    </div>
  );
};

LibrarianReaderEmbed.propTypes = {
  assetId: PropTypes.string,
};

LibrarianReaderEmbed.defaultProps = {
  assetId: null,
};

module.exports = LibrarianReaderEmbed;
